import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import {
  Mail, Phone, MapPin, Clock, ChevronDown, Check,
  ExternalLink, Facebook, Instagram, Linkedin, MessageCircle,
  Navigation
} from 'lucide-react';

/* ══════  DATA  ══════ */

const LOCATIONS = [
  {
    label: 'Head Office',
    city: 'Noida, Uttar Pradesh',
    address: 'F-375, Sector-36, Noida, UP 201301',
    tag: 'Headquarters',
    tagColor: 'bg-blue-100 text-blue-700',
    mapLink: 'https://maps.google.com/?q=F-375,+Sector+36,+Noida,+Uttar+Pradesh+201301',
    color: 'border-blue-200 bg-blue-50/40',
    iconBg: 'bg-blue-600',
  },
  {
    label: 'Gorakhpur Office',
    city: 'Gorakhpur, Uttar Pradesh',
    address: 'Hata Bazar, 3rd Floor, Kalpana Hardware Building, Shivpur, Mahurai, UP',
    tag: 'Branch',
    tagColor: 'bg-emerald-100 text-emerald-700',
    mapLink: 'https://share.google/0wAljKrg9v5T2uweP',
    color: 'border-emerald-200 bg-emerald-50/40',
    iconBg: 'bg-emerald-600',
  },
];

const CONTACT_CARDS = [
  {
    icon: Mail,
    label: 'Email Us',
    value: 'vibhraad@gmail.com',
    sub: 'We reply within 24 hours',
    href: 'mailto:vibhraad@gmail.com',
    color: 'bg-blue-600',
  },
  {
    icon: Phone,
    label: 'Call Us',
    value: '+91 7004535563',
    sub: 'Mon–Fri, 9 AM – 6 PM',
    href: 'tel:+917004535563',
    color: 'bg-indigo-600',
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: 'Chat on WhatsApp',
    sub: 'Quick project discussions',
    href: 'https://wa.me/7004535563',
    color: 'bg-green-600',
  },
];

const SERVICES = [
  'Attendance System', 'Web Development', 'Mobile App Development',
  'ERP & CRM Systems', 'POS Systems', 'Cloud Solutions',
  'AI & Machine Learning', 'Digital Marketing', 'E-commerce Solutions',
  'Custom Software Development', 'Consulting Services',
];

const OFFICE_HOURS = [
  { days: 'Monday – Friday', time: '9:00 AM – 6:00 PM' },
  { days: 'Saturday',        time: '10:00 AM – 4:00 PM' },
  { days: 'Sunday',          time: 'Closed' },
];

const FAQS = [
  {
    q: 'What is your typical project timeline?',
    a: 'Timelines vary by scope. Simple websites: 4–6 weeks. Complex enterprise apps or ERPs: 3–6 months. We provide a detailed timeline in your initial proposal.',
  },
  {
    q: 'What is your payment structure?',
    a: 'We use milestone-based payments: 50% upfront to begin work, and the remaining 50% upon final delivery. This ensures accountability on both sides.',
  },
  {
    q: 'Do you provide ongoing support?',
    a: 'Yes. All projects include a 30-day post-launch support period. Extended support and maintenance packages are available separately.',
  },
  {
    q: 'Can you work with international clients?',
    a: 'Absolutely. We have delivered projects for clients across 25+ countries. We use video calls, Slack, and project management tools for seamless remote collaboration.',
  },
];

/* ══════  HELPERS  ══════ */

const fade = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } } };

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
        <span className="text-sm font-semibold text-gray-900 leading-snug">{q}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="flex-shrink-0">
          <ChevronDown className="w-5 h-5 text-blue-500" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden">
            <p className="px-6 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-50 pt-3">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ══════  CONTACT FORM  ══════ */

function ContactForm() {
  const [state, handleSubmit] = useForm('mgvnbrve');

  if (state.succeeded) return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <Check className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
      <p className="text-gray-500 text-sm">We'll get back to you within 24 hours.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {[
        { id: 'name',    type: 'text',  label: 'Your Name',     placeholder: 'Rahul Sharma',       required: true },
        { id: 'email',   type: 'email', label: 'Email Address', placeholder: 'rahul@company.com',  required: true },
        { id: 'phone',   type: 'tel',   label: 'Phone Number',  placeholder: '+91 98765 43210',    required: false },
        { id: 'company', type: 'text',  label: 'Company Name',  placeholder: 'Your company (optional)', required: false },
      ].map(f => (
        <div key={f.id}>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {f.label} {f.required && <span className="text-red-500">*</span>}
          </label>
          <input id={f.id} name={f.id} type={f.type} placeholder={f.placeholder} required={f.required}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm transition-all bg-gray-50 focus:bg-white" />
          <ValidationError field={f.id} errors={state.errors} className="text-xs text-red-500 mt-1" />
        </div>
      ))}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Service Interested In <span className="text-red-500">*</span>
        </label>
        <select name="service" required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm transition-all bg-gray-50 focus:bg-white">
          <option value="">Select a service</option>
          {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Budget Range</label>
          <select name="budget"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm bg-gray-50 focus:bg-white">
            <option value="">Select…</option>
            <option value="under-50k">Under ₹50K</option>
            <option value="50k-1l">₹50K – ₹1L</option>
            <option value="1l-3l">₹1L – ₹3L</option>
            <option value="3l-5l">₹3L – ₹5L</option>
            <option value="above-5l">Above ₹5L</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Timeline</label>
          <select name="timeline"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm bg-gray-50 focus:bg-white">
            <option value="">Select…</option>
            <option value="urgent">Under 1 month</option>
            <option value="1-3months">1–3 months</option>
            <option value="3-6months">3–6 months</option>
            <option value="6+months">6+ months</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea name="message" required rows={5} placeholder="Tell us about your project, goals, and any specific requirements…"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm transition-all bg-gray-50 focus:bg-white resize-none" />
        <ValidationError field="message" errors={state.errors} className="text-xs text-red-500 mt-1" />
      </div>

      <button type="submit" disabled={state.submitting}
        className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20">
        {state.submitting
          ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
          : 'Send Message →'}
      </button>
    </form>
  );
}

/* ══════  MAIN PAGE  ══════ */

export default function ContactPage() {
  return (
    <div className="bg-white min-h-screen">

      {/* ── Hero ── */}
      <section className="relative bg-gray-950 py-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-700/20 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-indigo-600/15 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-blue-400 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" /> Response within 24 hours
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-5 leading-tight">
              Let's <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Connect</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Have a project in mind? Share your idea — we'll send a detailed proposal and cost estimate within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Contact Cards ── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid md:grid-cols-3 gap-5">
            {CONTACT_CARDS.map(({ icon: Icon, label, value, sub, href, color }, i) => (
              <motion.a
                key={label} href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group">
                <div className={`w-11 h-11 ${color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">{label}</div>
                  <div className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{value}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Locations ── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-10">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-3">Our Offices</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Visit Us</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {LOCATIONS.map((loc, i) => (
              <motion.div
                key={loc.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className={`rounded-3xl border-2 p-7 ${loc.color} hover:shadow-lg transition-all duration-300`}>
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-2 ${loc.tagColor}`}>
                      {loc.tag}
                    </span>
                    <h3 className="text-xl font-black text-gray-900">{loc.label}</h3>
                    <p className="text-blue-600 font-semibold text-sm">{loc.city}</p>
                  </div>
                  <div className={`w-12 h-12 ${loc.iconBg} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                </div>

                <p className="text-gray-600 text-sm leading-relaxed mb-6">{loc.address}</p>

                <a
                  href={loc.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-semibold text-sm px-5 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-all shadow-sm hover:shadow-md">
                  <Navigation className="w-4 h-4 text-blue-600" />
                  Get Directions
                  <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form + Hours ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid lg:grid-cols-5 gap-12 items-start">

            {/* Form — wider */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
              <h2 className="text-2xl font-black text-gray-900 mb-1">Send Us a Message</h2>
              <p className="text-gray-400 text-sm mb-8">Every enquiry gets a personal response within 24 hours.</p>
              <ContactForm />
            </motion.div>

            {/* Office hours + social */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-5">

              {/* Hours */}
              <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Office Hours</h3>
                </div>
                <div className="space-y-3">
                  {OFFICE_HOURS.map(({ days, time }) => (
                    <div key={days} className="flex justify-between items-center text-sm border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-600 font-medium">{days}</span>
                      <span className={`font-bold ${time === 'Closed' ? 'text-red-500' : 'text-gray-900'}`}>{time}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick response */}
              <div className="bg-blue-600 rounded-3xl p-6 text-white">
                <div className="text-2xl font-black mb-1">⚡ 24-Hour</div>
                <div className="text-blue-100 text-sm font-medium mb-3">Response Guarantee</div>
                <p className="text-blue-100 text-xs leading-relaxed">
                  We reply to every enquiry with a detailed proposal. No automated responses — just real people.
                </p>
              </div>

              {/* Social */}
              <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Follow Us</h3>
                <div className="flex gap-3">
                  {[
                    { icon: Facebook,      href: 'https://www.facebook.com/people/Vibhraad/61579581818694/', label: 'Facebook' },
                    { icon: Instagram,     href: 'https://instagram.com/vibhraad',                           label: 'Instagram' },
                    { icon: Linkedin,      href: 'https://www.linkedin.com/company/vibhraad/',               label: 'LinkedIn' },
                    { icon: MessageCircle, href: 'https://wa.me/7004535563',                               label: 'WhatsApp' },
                  ].map(({ icon: Icon, href, label }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                      className="w-10 h-10 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-xl flex items-center justify-center text-gray-500 hover:text-blue-600 transition-all shadow-sm">
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-5">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-3">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Common Questions</h2>
          </motion.div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} index={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
